/**
 * Ячейка справочника (§5.2, тип «Справочник»).
 *
 * Редактор — `Select` из библиотеки: поповер со значениями теми же чипами,
 * что в таблице, и «Очистить» внизу. Выбор значения мышью
 * или Enter-ом переводит поповер на ту же колонку следующей строки — это
 * главный жест массовой простановки (§5.3).
 */
import { useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { IconEye, IconEyeOff, Select, Tooltip, chipColor } from '../../../../components/index.js'
import { useApp } from '../../../../store/app-store.js'
import { tableTexts as t } from '../../texts.table.js'
import { withEnumBadges } from '../enum-badges.js'
import { enumColor, enumLabel, enumOptions } from '../values.js'
import type { CellProps } from './CellBox.js'

export function EnumCell(props: CellProps) {
  const { column, cell, ctx, editing, write, stepAfterCommit, commitAndRight, closeEditor, readOnly } = props
  // §5.2: значения в поповере показываются теми же бейджами, что в таблице.
  const options = useMemo(
    () => withEnumBadges(enumOptions(ctx.enums, column.enumSet), column.enumSet, ctx.enums),
    [ctx.enums, column.enumSet],
  )

  /* Из списка можно уйти в сам справочник: завести новое значение,
     переименовать или перекрасить существующее. Пункт видят только те, кому
     справочники вообще доступны. */
  const mayManage = useApp(
    (state) => state.bootstrap?.user.permissions.includes('catalog.enums.manage') ?? false,
  )
  const manage = mayManage && column.enumSet ? (
    <Link to={`/catalog/enums?set=${column.enumSet}`} onMouseDown={(event) => event.stopPropagation()}>
      {t.columnsMenu.manageEnum}
    </Link>
  ) : null

  const wrap = useRef<HTMLDivElement>(null)
  const direction = useRef<1 | -1>(1)
  const advancing = useRef(false)

  // Поповер открывается сразу, поэтому фокус переносим на его триггер:
  // иначе ↑↓ и буквы-хоткеи остались бы в сетке (§2.3, §5.3).
  useEffect(() => {
    if (!editing) return
    wrap.current?.querySelector<HTMLButtonElement>('button[role="combobox"]')?.focus()
  }, [editing])

  if (editing && !readOnly) {
    return (
      <div
        className="cell__editor"
        ref={wrap}
        onKeyDownCapture={(event) => {
          if (event.key === 'Enter') { direction.current = event.shiftKey ? -1 : 1; return }
          if (event.key === 'Tab') {
            // Tab фиксирует и уходит вправо, редактор не открывая (§5.3).
            event.preventDefault()
            event.stopPropagation()
            commitAndRight()
          }
        }}
      >
        <Select
          value={typeof cell.value === 'string' ? cell.value : null}
          options={options}
          open
          onOpenChange={(open) => {
            if (open) return
            if (advancing.current) { advancing.current = false; return }
            closeEditor()
          }}
          onChange={(next) => {
            advancing.current = true
            write(next)
            stepAfterCommit(direction.current)
            direction.current = 1
          }}
          label={column.srTitle ?? column.title}
          size="cell"
          variant="plain"
          emptyLabel={t.empty.dash}
          manage={manage}
        />
      </div>
    )
  }

  return <EnumValue {...props} />
}

/** Показ значения: всё справочное — подписью-чипом, иконок-ребусов нет. */
function EnumValue({ column, cell, ctx }: CellProps) {

  if (cell.empty) return <span className="cell__empty">{t.empty.dash}</span>

  const label = enumLabel(ctx.enums, column.enumSet, cell.value) ?? String(cell.value ?? '')
  // Цвет берётся из набора и значения (SEO, контекст, статусы): так в исходной
  // таблице и было — приоритет виден цветом, а не только текстом.
  const value = String(cell.value ?? '')
  const eye = column.code === 'visibility'
    ? (value === 'no' ? <IconEyeOff size={16} /> : <IconEye size={16} />)
    : null
  return (
    <span className="cell__value">
      <span
        className="enum-chip"
        data-set={column.enumSet}
        data-value={value}
        data-color={chipColor(enumColor(ctx.enums, column.enumSet, value))}
      >
        {eye ? <span className="enum-chip__icon" aria-hidden="true">{eye}</span> : null}
        {label}
      </span>
    </span>
  )
}
